<template>
  <Modal
    v-model="visibleSync"
    :title="modalTitle"
    class-name="external-api-modal"
    width="640"
    @on-visible-change="onVisibleChange"
  >
    <div v-if="editMode === EXTERNAL_API_MODE.JSON" class="external-api-modal-form">
      <div class="external-api-panel">
        <div
          class="external-api-panel__head"
          role="button"
          tabindex="0"
          @click="toggleSection('basic')"
          @keyup.enter="toggleSection('basic')"
        >
          <span class="external-api-panel__title-wrap">
            <span class="external-api-panel__icon external-api-panel__icon--info" aria-hidden="true">
              <FileTextOutlinedIcon :size="16" />
            </span>
            <span class="external-api-panel__title">基本信息</span>
          </span>
          <Icon
            :type="sectionExpanded.basic ? 'ios-arrow-up' : 'ios-arrow-down'"
            class="external-api-panel__chevron"
            size="16"
          />
        </div>
        <div v-show="sectionExpanded.basic" class="external-api-panel__body">
          <div class="external-api-modal-field">
            <label class="external-api-modal-field__label">名称</label>
            <Input
              v-model="form.name"
              class="external-api-modal-field__input"
              placeholder="请输入名称"
            />
            <p class="external-api-modal-field__hint">
              JSON 模式下可不填，兼容旧配置；未填时列表显示「未命名」
            </p>
          </div>
        </div>
      </div>

      <div class="external-api-panel">
        <div
          class="external-api-panel__head"
          role="button"
          tabindex="0"
          @click="toggleSection('json')"
          @keyup.enter="toggleSection('json')"
        >
          <span class="external-api-panel__title-wrap">
            <span class="external-api-panel__icon external-api-panel__icon--json" aria-hidden="true">
              <Icon type="logo-javascript" :size="16" />
            </span>
            <span class="external-api-panel__title">
              接口 JSON
              <span class="external-api-modal-field__required external-api-modal-field__required--suffix">*</span>
            </span>
          </span>
          <Icon
            :type="sectionExpanded.json ? 'ios-arrow-up' : 'ios-arrow-down'"
            class="external-api-panel__chevron"
            size="16"
          />
        </div>
        <div v-show="sectionExpanded.json" class="external-api-panel__body">
          <Input
            v-model="form.interfaceJsonText"
            type="textarea"
            :rows="12"
            class="external-api-modal-field__input external-api-modal-field__json"
            placeholder="请输入接口 JSON"
          />
        </div>
      </div>
    </div>

    <div v-else class="external-api-modal-form">
      <div class="external-api-panel">
        <div
          class="external-api-panel__head"
          role="button"
          tabindex="0"
          @click="toggleSection('plugin')"
          @keyup.enter="toggleSection('plugin')"
        >
          <span class="external-api-panel__title-wrap">
            <span class="external-api-panel__icon external-api-panel__icon--plugin" aria-hidden="true">
              <DeploymentUnitOutlinedIcon :size="16" />
            </span>
            <span class="external-api-panel__title">插件信息</span>
          </span>
          <Icon
            :type="sectionExpanded.plugin ? 'ios-arrow-up' : 'ios-arrow-down'"
            class="external-api-panel__chevron"
            size="16"
          />
        </div>
        <div v-show="sectionExpanded.plugin" class="external-api-panel__body">
          <div class="external-api-modal-field">
            <label class="external-api-modal-field__label">
              <span class="external-api-modal-field__required">*</span>
              名称
            </label>
            <Input
              v-model="form.name"
              class="external-api-modal-field__input"
              placeholder="请输入名称"
            />
          </div>
          <div class="external-api-modal-field">
            <label class="external-api-modal-field__label">描述</label>
            <Input
              v-model="form.description"
              type="textarea"
              :rows="3"
              :maxlength="EXTERNAL_API_DESCRIPTION_MAX"
              show-word-limit
              class="external-api-modal-field__input"
              placeholder="请输入描述"
            />
          </div>
          <div class="external-api-modal-field-row">
            <div class="external-api-modal-field external-api-modal-field--grow">
              <label class="external-api-modal-field__label">
                <span class="external-api-modal-field__required">*</span>
                接口地址
              </label>
              <Input
                v-model="form.url"
                class="external-api-modal-field__input"
                placeholder="请输入接口地址"
              />
            </div>
            <div class="external-api-modal-field external-api-modal-field--method">
              <label class="external-api-modal-field__label">方法</label>
              <Select v-model="form.method" class="external-api-modal-field__input" transfer>
                <Option v-for="m in HTTP_METHODS" :key="m" :value="m" :label="m" />
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div class="external-api-panel">
        <div
          class="external-api-panel__head"
          role="button"
          tabindex="0"
          @click="toggleSection('header')"
          @keyup.enter="toggleSection('header')"
        >
          <span class="external-api-panel__title-wrap">
            <span class="external-api-panel__icon external-api-panel__icon--header" aria-hidden="true">
              <Icon type="ios-options" :size="16" />
            </span>
            <span class="external-api-panel__title">Header参数</span>
          </span>
          <Icon
            :type="sectionExpanded.header ? 'ios-arrow-up' : 'ios-arrow-down'"
            class="external-api-panel__chevron"
            size="16"
          />
        </div>
        <div v-show="sectionExpanded.header" class="external-api-panel__body">
          <div class="external-api-param-table">
            <div class="external-api-param-table__head external-api-param-table__head--header">
              <span>名称</span>
              <span>值</span>
              <span />
            </div>
            <div
              v-for="(row, index) in form.headers"
              :key="'hdr-' + index"
              class="external-api-param-table__row external-api-param-table__row--header"
            >
              <Input v-model="row.name" placeholder="请输入名称" />
              <Select
                v-model="row.value"
                transfer
                filterable
                allow-create
                @on-create="onCreateHeaderValue"
              >
                <Option
                  v-for="opt in headerValueOptions"
                  :key="opt"
                  :value="opt"
                  :label="opt"
                />
              </Select>
              <span class="external-api-param-table__del" @click="removeHeaderRow(index)">
                <Icon type="md-close" size="18" />
              </span>
            </div>
          </div>
          <Button type="text" class="external-api-modal-add-row" @click="addHeaderRow">
            + 新增Header参数
          </Button>
        </div>
      </div>

      <div class="external-api-panel">
        <div
          class="external-api-panel__head"
          role="button"
          tabindex="0"
          @click="toggleSection('input')"
          @keyup.enter="toggleSection('input')"
        >
          <span class="external-api-panel__title-wrap">
            <span class="external-api-panel__icon external-api-panel__icon--params" aria-hidden="true">
              <Icon type="ios-options" :size="16" />
            </span>
            <span class="external-api-panel__title">
              配置输入参数
              <span class="external-api-modal-field__required external-api-modal-field__required--suffix">*</span>
            </span>
          </span>
          <Icon
            :type="sectionExpanded.input ? 'ios-arrow-up' : 'ios-arrow-down'"
            class="external-api-panel__chevron"
            size="16"
          />
        </div>
        <div v-show="sectionExpanded.input" class="external-api-panel__body">
          <div class="external-api-param-table">
            <div class="external-api-param-table__head external-api-param-table__head--input">
              <span>名称</span>
              <span>值</span>
              <span>描述</span>
              <span />
            </div>
            <div
              v-for="(row, index) in form.inputParams"
              :key="'inp-' + index"
              class="external-api-param-table__row external-api-param-table__row--input"
            >
              <Input v-model="row.name" placeholder="请输入名称" />
              <Input v-model="row.value" placeholder="请输入值" />
              <Input v-model="row.description" placeholder="请输入描述" />
              <span class="external-api-param-table__del" @click="removeInputParamRow(index)">
                <Icon type="md-close" size="18" />
              </span>
            </div>
          </div>
          <Button type="text" class="external-api-modal-add-row" @click="addInputParamRow">
            + 新增输入参数
          </Button>
        </div>
      </div>
    </div>

    <div slot="footer" class="external-api-modal-footer">
      <Button @click="close">取消</Button>
      <Button type="primary" @click="submit">确定</Button>
    </div>
  </Modal>
</template>

<script>
import DeploymentUnitOutlinedIcon from '../shared/DeploymentUnitOutlinedIcon.vue'
import FileTextOutlinedIcon from '../shared/FileTextOutlinedIcon.vue'
import {
  EXTERNAL_API_DESCRIPTION_MAX,
  EXTERNAL_API_MODE,
  HEADER_VALUE_OPTIONS,
  HTTP_METHODS,
} from '../../constants/externalApi'
import {
  normalizeExternalApiItem,
  parseInterfaceJsonText,
  resolveExternalApiItemForEdit,
  stringifyInterfaceJsonPretty,
} from '../../utils/externalApiHelpers'

function createDefaultItemForText() {
  return normalizeExternalApiItem({ mode: EXTERNAL_API_MODE.JSON })
}

function createDefaultHeaderRow() {
  return { name: '', value: 'application/json' }
}

function createDefaultInputParamRow() {
  return { name: '', value: '', description: '' }
}

function ensureHeaderRows(headers) {
  if (Array.isArray(headers) && headers.length) {
    return headers.map((row) => ({
      name: row.name != null ? String(row.name) : '',
      value: row.value != null ? String(row.value) : '',
    }))
  }
  return [createDefaultHeaderRow()]
}

function ensureInputParamRows(inputParams) {
  if (Array.isArray(inputParams) && inputParams.length) {
    return inputParams.map((row) => ({
      name: row.name != null ? String(row.name) : '',
      value: row.value != null ? String(row.value) : '',
      description: row.description != null ? String(row.description) : '',
    }))
  }
  return [createDefaultInputParamRow()]
}

function createEmptyForm() {
  return {
    id: '',
    name: '',
    description: '',
    interfaceJsonText: stringifyInterfaceJsonPretty(createDefaultItemForText()),
    method: 'POST',
    url: '',
    headers: [createDefaultHeaderRow()],
    inputParams: [createDefaultInputParamRow()],
  }
}

function createDefaultSectionExpanded() {
  return {
    basic: true,
    json: true,
    plugin: true,
    header: true,
    input: true,
  }
}

export default {
  name: 'ExternalApiModal',
  components: {
    DeploymentUnitOutlinedIcon,
    FileTextOutlinedIcon,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editIndex: {
      type: Number,
      default: -1,
    },
    initialData: {
      type: Object,
      default: null,
    },
    editMode: {
      type: String,
      default: EXTERNAL_API_MODE.JSON,
    },
  },
  data() {
    return {
      EXTERNAL_API_MODE,
      EXTERNAL_API_DESCRIPTION_MAX,
      HTTP_METHODS,
      form: createEmptyForm(),
      headerValueOptions: [...HEADER_VALUE_OPTIONS],
      sectionExpanded: createDefaultSectionExpanded(),
    }
  },
  computed: {
    visibleSync: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    modalTitle() {
      return this.editIndex >= 0 ? '编辑接口' : '增加接口'
    },
  },
  methods: {
    onVisibleChange(visible) {
      if (visible) {
        this.resetForm()
      }
    },
    toggleSection(key) {
      if (!this.sectionExpanded[key]) {
        this.$set(this.sectionExpanded, key, true)
        return
      }
      this.$set(this.sectionExpanded, key, !this.sectionExpanded[key])
    },
    resetForm() {
      const item = this.initialData
        ? resolveExternalApiItemForEdit(this.initialData, this.editMode)
        : normalizeExternalApiItem({ mode: this.editMode })
      this.form = {
        id: item.id,
        name: item.name,
        description: item.description,
        interfaceJsonText: stringifyInterfaceJsonPretty(item),
        method: item.method,
        url: item.url,
        headers: ensureHeaderRows(item.headers),
        inputParams: ensureInputParamRows(item.input_params),
      }
      this.sectionExpanded = createDefaultSectionExpanded()
    },
    onCreateHeaderValue(val) {
      const text = String(val || '').trim()
      if (!text) return
      if (!this.headerValueOptions.includes(text)) {
        this.headerValueOptions.push(text)
      }
    },
    addHeaderRow() {
      this.form.headers.push(createDefaultHeaderRow())
    },
    removeHeaderRow(index) {
      if (this.form.headers.length <= 1) {
        this.$set(this.form.headers, 0, createDefaultHeaderRow())
        return
      }
      this.form.headers.splice(index, 1)
    },
    addInputParamRow() {
      this.form.inputParams.push(createDefaultInputParamRow())
    },
    removeInputParamRow(index) {
      if (this.form.inputParams.length <= 1) {
        this.$set(this.form.inputParams, 0, createDefaultInputParamRow())
        return
      }
      this.form.inputParams.splice(index, 1)
    },
    close() {
      this.visibleSync = false
    },
    submit() {
      if (this.editMode === EXTERNAL_API_MODE.JSON) {
        const parsed = parseInterfaceJsonText(this.form.interfaceJsonText)
        if (!parsed.ok) {
          this.$Message.warning(parsed.message)
          return
        }
        this.$emit('submit', normalizeExternalApiItem({
          id: this.form.id,
          mode: EXTERNAL_API_MODE.JSON,
          name: this.form.name,
          interface_json: parsed.value,
        }))
        this.visibleSync = false
        return
      }

      const name = String(this.form.name || '').trim()
      if (!name) {
        this.$Message.warning('请填写名称')
        return
      }
      const url = String(this.form.url || '').trim()
      if (!url) {
        this.$Message.warning('请填写接口地址')
        return
      }
      const validInputParams = this.form.inputParams.filter(
        (row) => String(row.name || '').trim() !== '',
      )
      if (!validInputParams.length) {
        this.$Message.warning('请至少配置一个输入参数')
        return
      }
      const validHeaders = this.form.headers.filter(
        (row) => String(row.name || '').trim() !== '',
      )

      const item = normalizeExternalApiItem({
        id: this.form.id,
        mode: EXTERNAL_API_MODE.FORM,
        name,
        description: this.form.description,
        method: this.form.method,
        url,
        headers: validHeaders,
        input_params: validInputParams,
      })
      this.$emit('submit', item)
      this.visibleSync = false
    },
  },
}
</script>

<style lang="scss" scoped>
.external-api-modal-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.external-api-panel {
  border: 1px solid #e8eaec;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.external-api-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  padding: 0 16px;
  background: #f5f7fa;
  border-bottom: 1px solid #e8eaec;
  cursor: pointer;
  user-select: none;
}

.external-api-panel__title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.external-api-panel__icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: transparent;
  color: #5668f0;
  line-height: 1;
}

.external-api-panel__title {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 22px;
}

.external-api-panel__chevron {
  flex-shrink: 0;
  color: #909399;
}

.external-api-panel__body {
  padding: 16px;
}

.external-api-modal-field {
  display: flex;
  flex-direction: column;
  width: 100%;

  & + & {
    margin-top: 16px;
  }
}

.external-api-modal-field-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 16px;

  .external-api-modal-field {
    margin-top: 0;
  }
}

.external-api-modal-field--grow {
  flex: 1 1 auto;
  min-width: 0;
}

.external-api-modal-field--method {
  flex: 0 0 108px;
  width: 108px;
}

.external-api-modal-field__label {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #262626;
}

.external-api-modal-field__required {
  margin-right: 2px;
  color: #ed4014;

  &--suffix {
    margin-right: 0;
    margin-left: 2px;
  }
}

.external-api-modal-field__input {
  width: 100%;
}

.external-api-modal-field__hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.5;
}

.external-api-modal-field__json ::v-deep textarea.ivu-input {
  font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
}

.external-api-param-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.external-api-param-table__head,
.external-api-param-table__row {
  display: grid;
  align-items: center;
  gap: 8px;
}

.external-api-param-table__head--header,
.external-api-param-table__row--header {
  grid-template-columns: 1fr 1fr 28px;
}

.external-api-param-table__head--input,
.external-api-param-table__row--input {
  grid-template-columns: 1fr 1fr 1fr 28px;
}

.external-api-param-table__head {
  font-size: 14px;
  color: #262626;
  line-height: 22px;
}

.external-api-param-table__del {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  color: #c5c8ce;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: #ed4014;
    background: #fff1f0;
  }
}

.external-api-modal-add-row {
  margin-top: 8px;
  padding: 2px 8px !important;
  height: auto !important;
  border-radius: 4px !important;
  color: #5668f0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover:not([disabled]):not(.ivu-btn-disabled),
  &:focus:not([disabled]):not(.ivu-btn-disabled) {
    color: #3f51e8 !important;
    background: #f5f7ff !important;
    border: none !important;
    box-shadow: none !important;
  }
}

.external-api-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
</style>

<style lang="scss">
.external-api-modal {
  .ivu-modal-body {
    padding: 20px 24px 8px;
    max-height: 70vh;
    overflow-y: auto;
  }

  .ivu-modal-footer {
    border-top: none;
    padding: 8px 24px 20px;

    button + button {
      margin-left: 0;
    }
  }
}
</style>
